import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isApiError } from '../../lib/api';
import { createFlow, listFlows, listFlowTemplates, type FlowSummary, type FlowTemplateSummary, type FlowTriggerKind } from '../../lib/flows-api';
import styles from './flows.module.css';
import admin from './admin.module.css';

// A site's flows, and the template gallery to start a new one from. Flows
// answer a visitor before the AI does: a published flow that is on starts
// when its trigger matches, unless an agent already owns the conversation.

const TRIGGERS: Record<FlowTriggerKind, string> = {
  conversation_started: 'New conversation',
  message_received: 'Keyword message',
  manual: 'Manual only',
};

export function FlowsSection({ siteId }: { siteId: string }) {
  const navigate = useNavigate();
  const [flows, setFlows] = useState<FlowSummary[] | null>(null);
  const [templates, setTemplates] = useState<FlowTemplateSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([listFlows(siteId), listFlowTemplates(siteId)])
      .then(([f, t]) => {
        setFlows(f.flows);
        setTemplates(t.templates);
      })
      .catch((err) => setError(isApiError(err) && err.status === 403 ? 'Flows are not enabled for this account.' : 'Could not load flows.'));
  }, [siteId]);

  const create = async (templateId: string | null, name: string) => {
    setBusy(true);
    setError(null);
    try {
      const { id } = await createFlow(siteId, { name, templateId });
      navigate(`/admin/sites/${siteId}/flows/${id}`);
    } catch {
      setError('Could not create the flow.');
      setBusy(false);
    }
  };

  return (
    <>
      <section className={admin.section}>
        <h2 className={admin.title}>Flows</h2>
        <p className={admin.hint}>
          Automations that answer visitors before the AI: ask questions, branch on the answers, call your APIs, tag and hand conversations to your team.
          A flow starts only once it is published and switched on.
        </p>
        {error ? <p className={admin.error}>{error}</p> : null}
        {flows === null && !error ? <p className={admin.hint}>Loading…</p> : null}
        {flows?.length === 0 ? <p className={admin.hint}>No flows yet — start from a template below or a blank canvas.</p> : null}
        <div className={styles.cards}>
          {flows?.map((f) => (
            <button key={f.id} type="button" className={styles.card} onClick={() => navigate(`/admin/sites/${siteId}/flows/${f.id}`)}>
              <span className={styles.cardTitle}>{f.name}</span>
              <span className={styles.cardMeta}>
                <span className={f.enabled ? styles.statusOn : undefined}>{f.enabled ? '● On' : '○ Off'}</span>
                {' · '}
                {f.publishedVersion ? `v${f.publishedVersion}` : 'Draft'}
                {f.publishedVersion && f.unpublishedChanges ? ' (unpublished edits)' : ''}
                {f.triggerKind ? ` · ${TRIGGERS[f.triggerKind]}` : ''}
              </span>
              <span className={styles.cardMeta}>
                {f.nodeCount} steps · {f.runs30d.total} runs in 30 days
                {f.runs30d.failed ? ` · ${f.runs30d.failed} failed` : ''}
                {f.runs30d.live ? ` · ${f.runs30d.live} active` : ''}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className={admin.section}>
        <h2 className={admin.title}>New flow</h2>
        <div className={styles.cards}>
          <button type="button" className={styles.card} disabled={busy} onClick={() => void create(null, 'Untitled flow')}>
            <span className={styles.cardTitle}>Blank</span>
            <span className={styles.cardMeta}>Start with just a trigger.</span>
          </button>
          {templates.map((t) => (
            <button key={t.id} type="button" className={styles.card} disabled={busy} onClick={() => void create(t.id, t.name)}>
              <span className={styles.cardTitle}>{t.name}</span>
              <span className={styles.cardMeta}>{t.description}</span>
              <span className={styles.cardMeta}>
                {t.nodeCount} steps{t.triggerKind ? ` · ${TRIGGERS[t.triggerKind]}` : ''}
              </span>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}
